import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import './ArchivedClientsDialog.css';
import { useTranslation } from 'react-i18next';
import clientApi from 'services/client.service';

import {clientLifecycleError} from "./clientLifecycleError";

export default function ArchivedClientsDialog({onClose, onRestored, initialQuery=''}) {
  const {t}=useTranslation();
  const modalRef=useRef(null);
  const removeTabListener=useRef(()=>{});
  useEffect(()=>()=>removeTabListener.current(),[]);
  const containTabFocus=()=>{
    removeTabListener.current();
    const dialog=modalRef.current?.dialog;
    if(!dialog) return;
    const handleTab=event=>{
      if(event.key!=='Tab') return;
      const controls=Array.from(dialog.querySelectorAll(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])'
      )).filter(element=>!element.hidden && element.getAttribute('aria-hidden')!=='true');
      const first=controls[0],last=controls[controls.length-1];
      const active=dialog.ownerDocument.activeElement;
      if(!first){event.preventDefault();dialog.focus();return;}
      if(event.shiftKey && (active===first || active===dialog)){
        event.preventDefault();last.focus();
      } else if(!event.shiftKey && (active===last || active===dialog)){
        event.preventDefault();first.focus();
      }
    };
    dialog.addEventListener('keydown',handleTab);
    removeTabListener.current=()=>dialog.removeEventListener('keydown',handleTab);
  };
  const [draft,setDraft]=useState(initialQuery);
  const [query,setQuery]=useState(initialQuery);
  const [requestVersion,setRequestVersion]=useState(0);
  const [page,setPage]=useState(1);
  const [result,setResult]=useState({data:[],last_page:1});
  const [loading,setLoading]=useState(true);
  const [restoring,setRestoring]=useState(null);
  const [error,setError]=useState('');
  useEffect(()=>{
    let active=true;
    setLoading(true);setError('');
    clientApi.archived({q:query,page,limit:20}).then(response=>{
      if(active) setResult(response.data);
    }).catch(failure=>{
      if(active) setError(clientLifecycleError(failure,'Archived clients could not be loaded. Please retry.'));
    }).finally(()=>{if(active)setLoading(false);});
    return ()=>{active=false;};
  },[query,page,requestVersion]);
  const restore=async row=>{
    if(restoring) return;
    setRestoring(row.id);setError('');
    try {
      await clientApi.unarchive({id:row.id});
      setResult(previous=>({...previous,data:previous.data.filter(item=>item.id!==row.id)}));
      onRestored();
    } catch(failure) { setError(clientLifecycleError(failure,'Client could not be restored. Please retry.')); }
    finally { setRestoring(null); }
  };
  return <Modal ref={modalRef} show onShow={containTabFocus} animation={false} centered size="lg" backdrop="static" keyboard={false}
    className="archived-clients-dialog" style={{zIndex:1100}} aria-labelledby="archived-clients-title"
    onHide={onClose}>
      <div className="modal-header"><h2 id="archived-clients-title" className="modal-title fs-5">{t('Archived clients')}</h2>
        <button type="button" className="btn-close" aria-label={t('Close archived clients')} onClick={onClose} disabled={restoring!==null}/></div>
      <div className="modal-body">
        <p>{t('Unarchive a client to make them available for bookings again. Their existing history is preserved.')}</p>
        <form className="d-flex gap-2 mb-3" onSubmit={event=>{event.preventDefault();setQuery(draft.trim());setPage(1);setRequestVersion(value=>value+1);}}>
          <input className="form-control" aria-label={t('Search archived clients')} value={draft} onChange={event=>setDraft(event.target.value)}/>
          <button className="btn btn-primary" disabled={loading || restoring!==null}>{t('Search')}</button>
        </form>
        {error && <div role="alert" className="alert alert-danger">{t(error)}</div>}
        {loading ? <p role="status">{t('Loading archived clients…')}</p> : <>
          {result.data.length===0 ? <p>{t('No archived clients found.')}</p> : <ul className="list-group">
            {result.data.map(row=><li key={row.id} className="list-group-item d-flex justify-content-between align-items-center gap-2">
              <div><strong>{row.first_name} {row.last_name}</strong><div>{row.email || row.phone_number}</div></div>
              <button type="button" className="btn btn-outline-primary" aria-label={`${t('Unarchive')} ${row.first_name} ${row.last_name}`} disabled={restoring!==null} onClick={()=>restore(row)}>{t(restoring===row.id?'Restoring…':'Unarchive')}</button>
            </li>)}
          </ul>}
          <div className="d-flex justify-content-between mt-3">
            <button type="button" className="btn btn-outline-secondary" disabled={page<=1 || restoring!==null} onClick={()=>setPage(value=>value-1)}>{t('Previous')}</button>
            <span>{page} / {result.last_page || 1}</span>
            <button type="button" className="btn btn-outline-secondary" disabled={page>=result.last_page || restoring!==null} onClick={()=>setPage(value=>value+1)}>{t('Next')}</button>
          </div>
        </>}
      </div>
  </Modal>;
}

ArchivedClientsDialog.propTypes = {
  initialQuery: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onRestored: PropTypes.func.isRequired,
};
